/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';
import { controllable, Children as ChildrenPropTypes } from '@instructure/ui-prop-types';
import { FormPropTypes } from '@instructure/ui-form-field';
import { PositionPropTypes } from '@instructure/ui-position';
import { Calendar } from '@instructure/ui-calendar';
const propTypes = {
  renderLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  value: controllable(PropTypes.string),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  interaction: PropTypes.oneOf(['enabled', 'disabled', 'readonly']),
  isRequired: PropTypes.bool,
  isInline: PropTypes.bool,
  assistiveText: PropTypes.string,
  layout: PropTypes.oneOf(['stacked', 'inline']),
  width: PropTypes.string,
  display: PropTypes.oneOf(['inline-block', 'block']),
  inputRef: PropTypes.func,
  messages: PropTypes.arrayOf(FormPropTypes.message),
  placement: PositionPropTypes.placement,
  isShowingCalendar: PropTypes.bool,
  onRequestValidateDate: PropTypes.func,
  onRequestShowCalendar: PropTypes.func,
  onRequestHideCalendar: PropTypes.func,
  onRequestSelectNextDay: PropTypes.func,
  onRequestSelectPrevDay: PropTypes.func,
  onRequestRenderNextMonth: PropTypes.func,
  onRequestRenderPrevMonth: PropTypes.func,
  renderNavigationLabel: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  renderWeekdayLabels: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.node])),
  renderNextMonthButton: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  renderPrevMonthButton: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  children: ChildrenPropTypes.oneOf([Calendar.Day]),
  disabledDates: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  currentDate: PropTypes.string,
  disabledDateErrorMessage: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  invalidDateErrorMessage: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  locale: PropTypes.string,
  timezone: PropTypes.string,
  withYearPicker: PropTypes.object
};
const allowedProps = ['renderLabel', 'value', 'size', 'placeholder', 'onChange', 'onBlur', 'interaction', 'isRequired', 'isInline', 'assistiveText', 'layout', 'width', 'display', 'inputRef', 'messages', 'placement', 'isShowingCalendar', 'onRequestValidateDate', 'onRequestShowCalendar', 'onRequestHideCalendar', 'onRequestSelectNextDay', 'onRequestSelectPrevDay', 'onRequestRenderNextMonth', 'onRequestRenderPrevMonth', 'renderNavigationLabel', 'renderWeekdayLabels', 'renderNextMonthButton', 'renderPrevMonthButton', 'children', 'disabledDates', 'currentDate', 'disabledDateErrorMessage', 'invalidDateErrorMessage', 'locale', 'timezone'];
export { propTypes, allowedProps };